// L'ÉVOLUTION DE L'ADOPTION (migration 059) — l'onglet « Évolution » du tableau de bord.
//
//   1. la courbe de l'Adoption Score, une photo par mois (réticule + infobulle) ;
//   2. les recommandations IA, en barres empilées : à tester, en test,
//      confirmées, écartées — une seule teinte, du plus clair au plus foncé ;
//   3. une mini-courbe par pilier, avec sa note et sa variation (flèche + signe,
//      jamais la couleur seule) ;
//   4. le tableau des données, les dernières alertes, et les réglages.
import { useLayoutEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { ApiRequestError } from '../api/client';
import { Button, Chip, ErrorBanner, Eyebrow, Field, Note, Select, Spinner } from '../components/ui';
import { Drawer } from '../write/Drawer';
import {
  useAdoptionHistory, useAlertSettings, useMeasureNow, useSaveAlertSettings,
  type AlertRules, type EvolutionPoint, type Frequency,
} from './evolution';
import './evolution.css';

const errorOf = (e: unknown) => (e instanceof ApiRequestError ? e.message : 'Une erreur inattendue est survenue.');
const monthLabel = (m: string) =>
  new Date(`${m}-01T12:00:00Z`).toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
const monthLong = (m: string) =>
  new Date(`${m}-01T12:00:00Z`).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

const RECO_KEYS = ['confirmee', 'en_test', 'a_tester', 'ecartee'] as const;
const RECO_LABELS: Record<(typeof RECO_KEYS)[number], string> = {
  confirmee: 'Confirmées', en_test: 'En test', a_tester: 'À tester', ecartee: 'Écartées',
};

/** La largeur réelle d'un bloc : le graphique se dessine au pixel, lisible sur mobile. */
function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [w, setW] = useState(640);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const fit = (px: number) => setW(Math.max(260, Math.round(px)));
    // Une première mesure tout de suite, avant même l'observateur.
    fit(el.clientWidth);
    const ro = new ResizeObserver(([e]) => fit(e!.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w] as const;
}

/** Un rectangle aux coins du HAUT arrondis (4 px), carré sur la ligne de base. */
function topRounded(x: number, y: number, w: number, h: number, r = 4) {
  const rr = Math.min(r, h, w / 2);
  return `M${x},${y + h} V${y + rr} Q${x},${y} ${x + rr},${y} H${x + w - rr} Q${x + w},${y} ${x + w},${y + rr} V${y + h} Z`;
}

// ---------------------------------------------------------------------------
// LES GRAPHIQUES
// ---------------------------------------------------------------------------

function Tooltip({ p, x, width }: { p: EvolutionPoint; x: number; width: number }) {
  const left = Math.min(Math.max(8, x + 12), width - 210);
  return (
    <div className="evo-tip" style={{ left }} role="status">
      <strong>{monthLong(p.month)}</strong>
      <span><b>{p.global === null ? '—' : Math.round(p.global)}</b> Adoption Score</span>
      <span><b>{p.confidence === null ? '—' : `${p.confidence} %`}</b> confiance</span>
      {RECO_KEYS.map((k) => (
        <span key={k}><b>{p.recommendations[k]}</b> recommandations {RECO_LABELS[k].toLowerCase()}</span>
      ))}
    </div>
  );
}

function EvolutionCharts({ series }: { series: EvolutionPoint[] }) {
  const [box, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const n = series.length;
  const m = { l: 34, r: 44, t: 14, b: 24 };
  const H = 220;
  const plotW = width - m.l - m.r;
  const x = (i: number) => m.l + (n === 1 ? plotW / 2 : (plotW * i) / (n - 1));
  const y = (v: number) => m.t + (H - m.t - m.b) * (1 - v / 100);
  const pts = series.map((p, i) => ({ i, p })).filter(({ p }) => p.global !== null);
  const path = pts.map(({ i, p }, k) => `${k ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.global!).toFixed(1)}`).join(' ');
  const last = pts.at(-1);
  // Sur un écran étroit, un mois sur deux (ou trois) : le dernier toujours.
  const every = Math.max(1, Math.ceil(56 / (n > 1 ? plotW / (n - 1) : plotW)));
  const showMonth = (i: number) => (n - 1 - i) % every === 0;

  // Barres des recommandations : une colonne par mois, ≤ 24 px.
  const BH = 120;
  const bt = 10;
  const bb = 22;
  const maxTotal = Math.max(1, ...series.map((p) => RECO_KEYS.reduce((s, k) => s + p.recommendations[k], 0)));
  const barW = Math.min(24, Math.max(8, (n === 1 ? plotW : plotW / (n - 1)) * 0.4));
  const by = (v: number) => (BH - bt - bb) * (v / maxTotal);
  const noReco = series.every((p) => RECO_KEYS.every((k) => p.recommendations[k] === 0));

  const pick = (e: PointerEvent<SVGRectElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - rect.left + m.l;
    let best = 0;
    for (let i = 1; i < n; i += 1) if (Math.abs(x(i) - px) < Math.abs(x(best) - px)) best = i;
    setHover(best);
  };
  const keys = (e: KeyboardEvent) => {
    if (e.key === 'ArrowRight') setHover((h) => Math.min(n - 1, (h ?? -1) + 1));
    if (e.key === 'ArrowLeft') setHover((h) => Math.max(0, (h ?? n) - 1));
    if (e.key === 'Escape') setHover(null);
  };

  return (
    <div className="evo-charts" ref={box}>
      <div className="evo-chart">
        <h3 className="evo-chart-title">Adoption Score</h3>
        <svg width={width} height={H} role="img" aria-label={`Évolution de l’Adoption Score sur ${n} mois`}>
          {[0, 25, 50, 75, 100].map((t) => (
            <g key={t}>
              <line className="evo-grid" x1={m.l} x2={width - m.r} y1={y(t)} y2={y(t)} />
              <text className="evo-axis" x={m.l - 8} y={y(t) + 4} textAnchor="end">{t}</text>
            </g>
          ))}
          {series.map((p, i) => (showMonth(i) ? (
            <text key={p.month} className="evo-axis" x={x(i)} y={H - 6} textAnchor="middle">{monthLabel(p.month)}</text>
          ) : null))}
          {hover !== null ? <line className="evo-cross" x1={x(hover)} x2={x(hover)} y1={m.t} y2={H - m.b} /> : null}
          {path ? <path className="evo-line" d={path} /> : null}
          {pts.map(({ i, p }) => (
            <circle key={p.month} className={`evo-dot${hover === i ? ' evo-dot-on' : ''}`} cx={x(i)} cy={y(p.global!)} r={hover === i ? 6 : 4} />
          ))}
          {last ? (
            <text className="evo-end" x={x(last.i) + 10} y={y(last.p.global!) + 4}>{Math.round(last.p.global!)}</text>
          ) : null}
          <rect
            className="evo-hit"
            x={m.l - 10} y={m.t} width={plotW + 20} height={H - m.t - m.b}
            tabIndex={0}
            aria-label="Parcourir les mois avec les flèches"
            onPointerMove={pick}
            onPointerLeave={() => setHover(null)}
            onFocus={() => setHover(n - 1)}
            onBlur={() => setHover(null)}
            onKeyDown={keys}
          />
        </svg>
        {hover !== null && series[hover] ? <Tooltip p={series[hover]!} x={x(hover)} width={width} /> : null}
      </div>

      <div className="evo-chart">
        <div className="evo-chart-head">
          <h3 className="evo-chart-title">Recommandations IA</h3>
          {noReco ? null : (
            <span className="evo-legend">
              {RECO_KEYS.map((k) => (
                <span key={k}><i className={`evo-sw evo-reco-${k}`} aria-hidden="true" />{RECO_LABELS[k]}</span>
              ))}
            </span>
          )}
        </div>
        {/* Pas de recommandation du tout : une phrase plutôt qu'un cadre vide. */}
        {noReco ? (
          <p className="zy-texte-muet">Aucune recommandation IA pour l’instant : elles apparaissent quand des insights portent une recommandation.</p>
        ) : (
        <svg width={width} height={BH} role="img" aria-label="Recommandations IA par mois, par statut">
          <line className="evo-grid" x1={m.l} x2={width - m.r} y1={BH - bb} y2={BH - bb} />
          {series.map((p, i) => {
            let acc = 0;
            const segs = RECO_KEYS.map((k) => ({ k, v: p.recommendations[k] })).filter((s) => s.v > 0);
            return (
              <g key={p.month} onPointerEnter={() => setHover(i)} onPointerLeave={() => setHover(null)}>
                <rect x={x(i) - barW} y={bt} width={barW * 2} height={BH - bt - bb} fill="transparent" />
                {segs.map((s, j) => {
                  const h = by(s.v);
                  const yTop = BH - bb - acc - h;
                  acc += h;
                  const gap = j < segs.length - 1 ? 2 : 0;
                  const isTop = j === segs.length - 1;
                  return isTop ? (
                    <path key={s.k} className={`evo-reco-${s.k}`} d={topRounded(x(i) - barW / 2, yTop, barW, Math.max(1, h - gap))} />
                  ) : (
                    <rect key={s.k} className={`evo-reco-${s.k}`} x={x(i) - barW / 2} y={yTop + gap} width={barW} height={Math.max(1, h - gap)} />
                  );
                })}
                {showMonth(i) ? <text className="evo-axis" x={x(i)} y={BH - 6} textAnchor="middle">{monthLabel(p.month)}</text> : null}
              </g>
            );
          })}
        </svg>
        )}
      </div>
    </div>
  );
}

/** Une mini-courbe par pilier : la tendance, la note, la variation. */
function PillarMultiples({ series, threshold }: { series: EvolutionPoint[]; threshold: number }) {
  const lastP = series.at(-1);
  if (!lastP) return null;
  const prev = series.at(-2);
  const W = 140;
  const H = 30;
  return (
    <ul className="evo-pillars">
      {/* Un pilier jamais noté n'a pas d'histoire à montrer. */}
      {lastP.pillars.filter((pl) => series.some((p) => p.pillars.some((x) => x.pillarId === pl.pillarId && x.score !== null))).map((pl) => {
        const values = series.map((p) => p.pillars.find((x) => x.pillarId === pl.pillarId)?.score ?? null);
        const known = values.map((v, i) => ({ v, i })).filter((d) => d.v !== null) as { v: number; i: number }[];
        const sx = (i: number) => (series.length === 1 ? W / 2 : 4 + ((W - 8) * i) / (series.length - 1));
        const sy = (v: number) => 4 + (H - 8) * (1 - v / 10);
        const d = known.map((k, j) => `${j ? 'L' : 'M'}${sx(k.i).toFixed(1)},${sy(k.v).toFixed(1)}`).join(' ');
        const before = prev?.pillars.find((x) => x.pillarId === pl.pillarId)?.score ?? null;
        const delta = pl.score !== null && before !== null ? Math.round((pl.score - before) * 10) / 10 : null;
        const warn = pl.isCapped || (pl.score !== null && pl.score < threshold);
        return (
          <li key={pl.pillarId}>
            <span className="evo-pl-name">{pl.name}</span>
            <svg width={W} height={H} aria-hidden="true">
              {d ? <path className="evo-spark" d={d} /> : null}
              {known.length ? <circle className="evo-spark-dot" cx={sx(known.at(-1)!.i)} cy={sy(known.at(-1)!.v)} r={3.5} /> : null}
            </svg>
            <span className="evo-pl-score">{pl.score === null ? '—' : pl.score.toFixed(1)}</span>
            <span className={`evo-pl-delta${delta === null ? '' : delta > 0 ? ' evo-up' : delta < 0 ? ' evo-down' : ''}`}>
              {delta === null ? '—' : `${delta > 0 ? '▲ +' : delta < 0 ? '▼ ' : '= '}${delta.toFixed(1).replace('-', '−')}`}
            </span>
            <span className="evo-pl-warn">
              {warn ? <span title={pl.isCapped ? 'Plafonné par un signal critique' : `Sous ${threshold}/10`}>⚠ {pl.isCapped ? 'plafonné' : `sous ${threshold}`}</span> : null}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function DataTable({ series }: { series: EvolutionPoint[] }) {
  const names = series.at(-1)?.pillars ?? [];
  return (
    <details className="evo-table">
      <summary>Voir les données en tableau</summary>
      <div className="zy-table-cadre">
        <table className="zy-table zy-table--dense">
          <thead>
            <tr>
              <th>Mois</th><th>Score</th><th>Confiance</th>
              {names.map((p) => <th key={p.pillarId}>{p.name}</th>)}
              {RECO_KEYS.map((k) => <th key={k}>Reco. {RECO_LABELS[k].toLowerCase()}</th>)}
            </tr>
          </thead>
          <tbody>
            {series.map((s) => (
              <tr key={s.month}>
                <td>{monthLong(s.month)}</td>
                <td>{s.global === null ? '—' : Math.round(s.global)}</td>
                <td>{s.confidence === null ? '—' : `${s.confidence} %`}</td>
                {names.map((n) => {
                  const v = s.pillars.find((x) => x.pillarId === n.pillarId)?.score;
                  return <td key={n.pillarId}>{v === null || v === undefined ? '—' : v.toFixed(1)}</td>;
                })}
                {RECO_KEYS.map((k) => <td key={k}>{s.recommendations[k]}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

// ---------------------------------------------------------------------------
// LES RÉGLAGES D'ALERTES — aussi dans Options › Alertes
// ---------------------------------------------------------------------------

const FREQ_LABELS: Record<Frequency, string> = {
  hebdomadaire: 'Chaque semaine', mensuelle: 'Chaque mois', trimestrielle: 'Chaque trimestre', aucune: 'Jamais (désactivée)',
};

export function AlertSettingsForm({ projectId, onDone }: { projectId: string; onDone?: () => void }) {
  const q = useAlertSettings(projectId);
  const save = useSaveAlertSettings(projectId);
  const [draft, setDraft] = useState<{ frequency: Frequency; rules: AlertRules; recipients: string } | null>(null);
  if (q.isPending) return <Spinner label="Chargement des alertes" />;
  if (q.isError || !q.data) return <ErrorBanner>{errorOf(q.error)}</ErrorBanner>;
  const s = draft ?? { frequency: q.data.settings.frequency, rules: q.data.settings.rules, recipients: q.data.settings.recipients.join(', ') };
  const set = (patch: Partial<typeof s>) => setDraft({ ...s, ...patch });
  const rule = <K extends keyof AlertRules>(k: K, patch: Partial<AlertRules[K]>) =>
    set({ rules: { ...s.rules, [k]: { ...s.rules[k], ...patch } } });
  const ro = !q.data.canWrite;

  return (
    <form
      className="write-form evo-settings"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate(
          { frequency: s.frequency, rules: s.rules, recipients: s.recipients.split(/[,;\s]+/).filter(Boolean) },
          { onSuccess: () => { setDraft(null); onDone?.(); } },
        );
      }}
    >
      <label className="zy-field">
        <span className="zy-field__label">Mesure automatique</span>
        <Select value={s.frequency} disabled={ro} onChange={(e) => set({ frequency: e.target.value as Frequency })}>
          {(Object.keys(FREQ_LABELS) as Frequency[]).map((f) => <option key={f} value={f}>{FREQ_LABELS[f]}</option>)}
        </Select>
        <span className="zy-field__aide">
          À chaque mesure, l’adoption est recalculée et photographiée : c’est ce qui trace la courbe.
          {q.data.settings.nextRunAt ? ` Prochaine : ${new Date(q.data.settings.nextRunAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}.` : ''}
        </span>
      </label>

      <fieldset className="evo-rules" disabled={ro}>
        <legend className="zy-field__label">Alertes</legend>
        <label className="ppl-check">
          <input className="zy-check" type="checkbox" checked={s.rules.newMeasure.enabled} onChange={(e) => rule('newMeasure', { enabled: e.target.checked })} />
          Le bilan de chaque nouvelle mesure
        </label>
        <label className="ppl-check">
          <input className="zy-check" type="checkbox" checked={s.rules.scoreDrop.enabled} onChange={(e) => rule('scoreDrop', { enabled: e.target.checked })} />
          Le score baisse de plus de
          <input className="zy-input evo-num" type="number" min={1} max={50} value={s.rules.scoreDrop.points}
            onChange={(e) => rule('scoreDrop', { points: Number(e.target.value) })} aria-label="Points de baisse" />
          points
        </label>
        <label className="ppl-check">
          <input className="zy-check" type="checkbox" checked={s.rules.pillarBelow.enabled} onChange={(e) => rule('pillarBelow', { enabled: e.target.checked })} />
          Un pilier passe sous
          <input className="zy-input evo-num" type="number" min={1} max={9.5} step={0.5} value={s.rules.pillarBelow.threshold}
            onChange={(e) => rule('pillarBelow', { threshold: Number(e.target.value) })} aria-label="Seuil sur 10" />
          /10, ou devient plafonné
        </label>
        <label className="ppl-check">
          <input className="zy-check" type="checkbox" checked={s.rules.recoPending.enabled} onChange={(e) => rule('recoPending', { enabled: e.target.checked })} />
          Des recommandations IA restent « à tester » plus de
          <input className="zy-input evo-num" type="number" min={7} max={365} value={s.rules.recoPending.days}
            onChange={(e) => rule('recoPending', { days: Number(e.target.value) })} aria-label="Jours" />
          jours
        </label>
      </fieldset>

      <Field
        label="Destinataires des e-mails"
        value={s.recipients}
        disabled={ro}
        onChange={(e) => set({ recipients: e.target.value })}
        placeholder="Les responsables du projet"
        hint="Séparés par des virgules. Vide : les responsables du projet et les administrateurs."
      />
      {!q.data.mail ? (
        <Note tone="lecture">L’envoi d’e-mails n’est pas configuré (back-office › E-mail) : les alertes s’affichent seulement dans l’appli.</Note>
      ) : null}
      {save.error ? <ErrorBanner>{errorOf(save.error)}</ErrorBanner> : null}
      {!ro ? <Button type="submit" variant="accent" loading={save.isPending} disabled={!draft}>Enregistrer les alertes</Button> : null}
    </form>
  );
}

// ---------------------------------------------------------------------------
// L'ONGLET
// ---------------------------------------------------------------------------

export function EvolutionPanel({ projectId }: { projectId: string }) {
  const q = useAdoptionHistory(projectId);
  const measure = useMeasureNow(projectId);
  const [settingsOpen, setSettingsOpen] = useState(false);
  if (q.isPending) return <Spinner label="Chargement de l’évolution" />;
  if (q.isError || !q.data) return <ErrorBanner>{errorOf(q.error)}</ErrorBanner>;
  const d = q.data;
  const s = d.settings;
  const first = d.series[0];
  const last = d.series.at(-1);
  const change = first && last && first !== last && first.global !== null && last.global !== null
    ? Math.round(last.global - first.global) : null;

  return (
    <section className="zy-card evo">
      <div className="evo-head">
        <div>
          <Eyebrow>Évolution</Eyebrow>
          <h2 className="t-h2">L’adoption, mois après mois</h2>
          <p className="card-lede">
            Une photo à chaque mesure : le score, chaque pilier, et où en sont les recommandations de l’IA.
            {change !== null ? ` Depuis ${monthLong(first!.month)} : ${change >= 0 ? '+' : ''}${change} points.` : ''}
          </p>
        </div>
        <div className="evo-actions">
          {d.canWrite ? (
            <Button variant="accent" loading={measure.isPending} onClick={() => measure.mutate()}>Mesurer maintenant</Button>
          ) : null}
          <Button variant="ghost" onClick={() => setSettingsOpen(true)}>🔔 Alertes</Button>
        </div>
      </div>
      <p className="zy-texte-muet">
        {s.frequency === 'aucune'
          ? 'Mesure automatique désactivée.'
          : `Mesure automatique ${FREQ_LABELS[s.frequency].toLowerCase()}${s.nextRunAt ? ` — prochaine le ${new Date(s.nextRunAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}` : s.configured ? '' : ' (à activer dans les alertes)'}.`}
        {d.sample ? ' Historique d’exemple : ce projet de démonstration n’a pas encore de vraie histoire.' : ''}
      </p>
      {measure.data ? (
        <p className="zy-texte-muet" role="status">
          Mesure faite{measure.data.alerts.length ? ` : ${measure.data.alerts.length} alerte${measure.data.alerts.length > 1 ? 's' : ''}` : ''}
          {measure.data.emailed ? ', envoyée(s) par e-mail.' : '.'}
        </p>
      ) : null}
      {measure.error ? <ErrorBanner>{errorOf(measure.error)}</ErrorBanner> : null}

      {d.series.length === 0 ? (
        <p className="card-note">
          Pas encore de mesure. Construisez le tableau d’adoption ou cliquez sur « Mesurer maintenant » : chaque mesure
          laisse une photo, et la courbe se dessine dès le deuxième mois.
        </p>
      ) : (
        <>
          <EvolutionCharts series={d.series} />
          <h3 className="evo-chart-title">Les piliers d’adoption</h3>
          <PillarMultiples series={d.series} threshold={s.rules.pillarBelow.threshold} />
          <DataTable series={d.series} />
        </>
      )}

      {d.events.length > 0 ? (
        <div className="evo-events">
          <h3 className="evo-chart-title">Dernières alertes</h3>
          <ul>
            {d.events.map((e) => (
              <li key={e.id}>
                <Chip state={e.severity === 'grave' ? 'erreur' : e.severity === 'attention' ? 'avertissement' : 'succes'}>
                  {e.severity === 'grave' ? '⚠ grave' : e.severity === 'attention' ? '• à surveiller' : '✓ info'}
                </Chip>
                <span>{e.message}</span>
                <span className="inv-sub">{new Date(e.at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <Drawer open={settingsOpen} title="Alertes d’adoption" description="La mesure automatique et ce qui mérite de vous prévenir." onClose={() => setSettingsOpen(false)}>
        <AlertSettingsForm projectId={projectId} onDone={() => setSettingsOpen(false)} />
      </Drawer>
    </section>
  );
}
